import { useMemo } from 'react';
import type { StylesConfig } from 'react-select';
import { useTheme } from '../../contexts/ThemeContext';

/** Option shape used by the app's react-select instances. */
export interface VpSelectOption {
  value: number | string;
  label: string;
}

interface Palette {
  controlBg: string;
  controlBgFocused: string;
  controlBorder: string;
  controlHoverBorder: string;
  optionBg: string;
  optionHover: string;
  text: string;
  menuBg: string;
  menuBorder: string;
  menuShadow: string;
  multiBg: string;
  multiFg: string;
  placeholder: string;
}

const lightPalette: Palette = {
  controlBg: '#f9fafc',
  controlBgFocused: '#ffffff',
  controlBorder: '#e9ebec',
  controlHoverBorder: '#ced4da',
  optionBg: '#ffffff',
  optionHover: '#e0f1cc',
  text: '#212529',
  menuBg: '#ffffff',
  menuBorder: '#e9ebec',
  menuShadow: '0 5px 10px rgba(30, 32, 37, 0.12)',
  multiBg: '#e0f1cc',
  multiFg: '#348b00',
  placeholder: '#878a99',
};

const darkPalette: Palette = {
  controlBg: '#212529',
  controlBgFocused: '#2a2f34',
  controlBorder: '#32383e',
  controlHoverBorder: '#3e444a',
  optionBg: '#212529',
  optionHover: '#2a2f34',
  text: '#ced4da',
  menuBg: '#212529',
  menuBorder: '#32383e',
  menuShadow: '0 5px 10px rgba(0, 0, 0, 0.4)',
  multiBg: 'rgba(61, 163, 0, 0.15)',
  multiFg: '#4db814',
  placeholder: '#878a99',
};

function buildStyles(p: Palette): StylesConfig<VpSelectOption, boolean> {
  return {
    // SP-8a follow-up: compact size (~31px) per uniformare con form-control
    // small Bootstrap. Riduzioni: minHeight 38→30, fontSize 0.875→0.8125rem,
    // option padding 8→4px verticali.
    control: (base, state) => ({
      ...base,
      minHeight: '30px',
      background: state.isFocused ? p.controlBgFocused : p.controlBg,
      borderColor: state.isFocused ? '#3da300' : p.controlBorder,
      boxShadow: state.isFocused ? '0 0 0 3px rgba(61, 163, 0, 0.25)' : 'none',
      borderRadius: '4px',
      fontSize: '0.8125rem',
      color: p.text,
      '&:hover': { borderColor: p.controlHoverBorder },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 6px' }),
    indicatorsContainer: (base) => ({ ...base, height: '28px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
    clearIndicator: (base) => ({ ...base, padding: '4px' }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3da300' : state.isFocused ? p.optionHover : p.optionBg,
      color: state.isSelected ? '#fff' : p.text,
      fontSize: '0.8125rem',
      padding: '4px 8px',
    }),
    singleValue: (base) => ({ ...base, color: p.text }),
    input: (base) => ({ ...base, color: p.text, margin: 0, padding: 0 }),
    multiValue: (base) => ({ ...base, backgroundColor: p.multiBg, borderRadius: '3px' }),
    multiValueLabel: (base) => ({ ...base, color: p.multiFg, fontSize: '11px', fontWeight: 500, padding: '0 4px' }),
    multiValueRemove: (base) => ({
      ...base,
      color: p.multiFg,
      '&:hover': { backgroundColor: '#3da300', color: '#fff' },
    }),
    placeholder: (base) => ({ ...base, color: p.placeholder, fontSize: '0.8125rem' }),
    menu: (base) => ({
      ...base,
      background: p.menuBg,
      border: `1px solid ${p.menuBorder}`,
      boxShadow: p.menuShadow,
      borderRadius: '4px',
      zIndex: 5,
    }),
  };
}

/**
 * Shared react-select styling aligned to the Velzon design standard
 * (SP-5h): green focus ring/active, cool-grey surfaces, 13px text.
 * Static export — kept for legacy callers that don't have access to the
 * ThemeContext (e.g. unit tests). Always light palette.
 */
export const velzonSelectStyles: StylesConfig<VpSelectOption, boolean> = buildStyles(lightPalette);

/**
 * SP-7b — dark-mode-aware hook. Use this in components rendered inside the
 * dashboard layout (which is always wrapped by ThemeProvider). Returns the
 * light or dark palette based on the current theme.
 */
export function useVelzonSelectStyles(): StylesConfig<VpSelectOption, boolean> {
  const { mode } = useTheme();
  return useMemo(() => buildStyles(mode === 'dark' ? darkPalette : lightPalette), [mode]);
}
